import {
  type ContigInfoFragment,
  useGetContigLazyQuery,
  useSearchContigLazyQuery,
} from "application/src/modules/entities/graphql/contig-queries.gql.ts";
import { flatten, groupBy, is, mapObjIndexed, values } from "ramda";
import { type ForwardedRef, type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { type AriaComboBoxProps, type Key, useComboBox, useFilter } from "react-aria";
import { Item, Section, useComboBoxState } from "react-stately";
import { useDebounce } from "use-debounce";
import { HStack, ListBox, LoadingSpinner, MenuOptionItem, Popover } from "widgets";
import styleForm from "widgets/form.module.css";
import { useDOMRef } from "widgets/hooks";
import { forwardRef } from "widgets/utils";

import style from "@/modules/entities/styles/RegionField.module.css";

interface RegionFieldContigComboBox<T> extends Omit<AriaComboBoxProps<T>, "children"> {
  genomeIds?: number[] | null;
  selectedKey?: Key;
  width?: number;
  popoverWidth?: number;
  className?: string;
  onContigChange?: (contig: ContigInfoFragment | null) => void;
  action?: ReactElement;
}

interface LoadedData {
  name: string;
  items: ContigInfoFragment[];
}

const RegionFieldContigComboBoxRender = (
  props: RegionFieldContigComboBox<LoadedData>,
  ref: ForwardedRef<HTMLInputElement>,
): ReactNode => {
  // Use for Popover position
  const boxRef = useRef<HTMLDivElement>(null);

  const buttonRef = useRef(null);
  const inputRef = useDOMRef(ref);
  const listBoxRef = useRef(null);
  const popoverRef = useRef(null);
  const {
    genomeIds,
    onContigChange,
    onSelectionChange,
    selectedKey,
    action,
    width,
    popoverWidth,
    isDisabled,
    onInputChange,
  } = props;

  // ComboBox
  const { contains } = useFilter({ sensitivity: "base" });
  const [get] = useGetContigLazyQuery();
  const [search] = useSearchContigLazyQuery();

  const [items, setItems] = useState<LoadedData[]>([]);
  const [filterText, setFilterText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedFilterText] = useDebounce(filterText, 300);

  useEffect(() => {
    // Reset filter text if `selectedKey` is not set.
    // This might happen when parent component clears the selected key.
    if (!selectedKey) {
      setFilterText("");
    }
  }, [selectedKey]);

  useEffect(() => {
    async function load(filterText = "") {
      setIsLoading(true);

      if (!is(Array, genomeIds) || genomeIds.length === 0) {
        setItems([]);
        setFilterText("");
        return;
      }

      const items = flatten(
        await Promise.all(
          genomeIds.map(async (id) => {
            const { data } = await search({
              variables: { searchTerm: filterText ?? "", limit: 50, genomeId: id },
            });
            return (data?.search.contigSearch.contigs ?? []).filter((contig) => contig != null);
          }),
        ),
      );

      if (items.length === 0) {
        setItems([]);
        return;
      }

      // If the selected key could not be found in the list of contigs, fetch it and prepend it to the list.
      if (selectedKey && !items.some((genome) => genome.id === Number(selectedKey))) {
        const res = await get({ variables: { contigId: Number(selectedKey) } });
        if (res.data?.contig && genomeIds.find((id) => id === res.data?.contig?.organism.id)) {
          items.unshift(res.data.contig);
        }
      }

      if (selectedKey && filterText === "") {
        const selectedItem = items.find((contig) => contig.id === Number(selectedKey));

        // Set if default key does exist, remove otherwise.
        if (selectedItem) {
          onContigChange?.(selectedItem);
          onSelectionChange?.(selectedItem.id);

          const types = groupBy(
            (item) => `${item.organism?.name} - ${item.sequenceType ?? "Unknown Sequence Type"}`,
            items,
          );
          const itemMap = mapObjIndexed((items, name) => ({ name, items: items! }), types);
          setItems([...values(itemMap)]);
          setFilterText(selectedItem?.accession);
          return;
        }

        // Reset because contig not found in the list.
        // Maybe any genomes up in the tree changed
        onContigChange?.(null);
        onSelectionChange?.(null);
      }

      const types = groupBy(
        (item) => `${item.organism?.name} - ${item.sequenceType ?? "Unknown Sequence Type"}`,
        items,
      );
      const itemMap = mapObjIndexed((items, name) => ({ name, items: items! }), types);
      setItems([...values(itemMap)]);
    }
    load(debouncedFilterText).then(() => setIsLoading(false));
  }, [debouncedFilterText, selectedKey, genomeIds, get, search, onSelectionChange, onContigChange]);

  const state = useComboBoxState({
    ...props,
    defaultFilter: contains,
    items,
    inputValue: filterText,
    onInputChange: useCallback(
      (filterText: string) => {
        setFilterText(filterText);
        onInputChange?.(filterText);
      },
      [onInputChange],
    ),
    onSelectionChange: useCallback(
      (key: Key | null) => {
        const flattenedItems = items.flatMap((section) => section.items);
        const selectedItem = flattenedItems.find((item) => Number(key) === Number(item.id));
        if (selectedItem) {
          setFilterText(selectedItem.accession);
        }
        onContigChange?.(selectedItem ?? null);
        onSelectionChange?.(key);
      },
      [items, onContigChange, onSelectionChange],
    ),
    selectedKey: selectedKey ? String(selectedKey) : null,
    // eslint-disable-next-line react/no-unstable-nested-components
    children: (section) => (
      <Section items={section.items} title={section.name} key={section.name}>
        {(item) => (
          <Item key={String(item.id)} textValue={item.accession}>
            <MenuOptionItem title={item.accession} description={item.name} />
          </Item>
        )}
      </Section>
    ),
  });

  const { inputProps, listBoxProps } = useComboBox(
    {
      ...props,
      buttonRef,
      inputRef,
      listBoxRef,
      popoverRef,
      "aria-label": props["aria-label"] ?? "Contig Region Selector",
    },
    state,
  );

  return (
    <>
      <HStack ref={boxRef} spacing={3} style={{ width }}>
        <input {...inputProps} className={style.input} ref={inputRef} placeholder={"Contig"} />
        {isLoading && <LoadingSpinner size={16} />}
        {!!action && !isDisabled && action}
      </HStack>
      <Popover ref={popoverRef} state={state} triggerRef={boxRef} offset={5} placement="bottom start">
        <ListBox
          {...listBoxProps}
          listBoxRef={listBoxRef}
          state={state}
          className={styleForm.menu}
          style={{ minWidth: popoverWidth ?? width ?? 260 }}
        />
      </Popover>
    </>
  );
};

export const RegionFieldContigComboBox = forwardRef(RegionFieldContigComboBoxRender);
